import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// SHARED ZOD SCHEMAS — MediCorePay
// Core business entity schemas for form validation across all apps
// ═══════════════════════════════════════════════════════════════════════════

// ── Reusable primitives ──────────────────────────────────────────────────

const indianPhone = z
  .string()
  .regex(/^[6-9]\d{9}$/, "Invalid Indian phone number");

const indianPincode = z
  .string()
  .regex(/^[1-9][0-9]{5}$/, "Invalid 6-digit pincode");

const gstNumber = z
  .string()
  .regex(
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
    "Invalid GSTIN format",
  )
  .optional()
  .or(z.literal(""));

const panNumber = z
  .string()
  .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format")
  .optional()
  .or(z.literal(""));

const positiveDecimal = z.coerce.number().min(0, "Must be a positive number");

const positiveInt = z.coerce
  .number()
  .int()
  .min(0, "Must be a positive integer");

// ── Auth ─────────────────────────────────────────────────────────────────

export const loginSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = z
  .object({
    firstName: z.string().min(2, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Valid email required"),
    phone: indianPhone,
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Must contain an uppercase letter")
      .regex(/[0-9]/, "Must contain a number"),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords must match",
  });
export type RegisterInput = z.infer<typeof registerSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().email("Enter a valid email"),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords must match",
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// ── Product ──────────────────────────────────────────────────────────────

export const productSchema = z.object({
  name: z.string().min(2, "Product name is required"),
  sku: z.string().min(1, "SKU is required"),
  barcode: z.string().optional(),
  categoryId: z.string().uuid().optional(),
  category: z.string().optional(),
  subcategory: z.string().optional(),
  description: z.string().optional(),
  manufacturer: z.string().optional(),
  purchasePrice: positiveDecimal,
  sellingPrice: positiveDecimal,
  mrp: positiveDecimal,
  ptr: positiveDecimal.optional(),
  gstPercent: z.coerce.number().min(0).max(100).default(5),
  scheduleType: z.enum(["GENERAL", "H", "H1", "X", "G"]).default("GENERAL"),
  unit: z.string().default("strip"),
  strength: z.string().optional(),
  strengthUnit: z.string().optional(),
  baseUnitType: z
    .enum(["TABLET", "CAPSULE", "SYRUP", "INJECTION", "OTHER"])
    .default("TABLET"),
  allowLooseSale: z.boolean().default(true),
  minSaleQuantity: positiveInt.default(1),
  status: z.enum(["active", "inactive"]).default("active"),
  composition: z.string().optional(),
  hsnCode: z.string().optional(),
  saltComposition: z.string().optional(),
  maxStockLevel: positiveInt.optional(),
  minStockLevel: positiveInt.optional(),
  isBatchTracked: z.boolean().default(true),
  isNarcotic: z.boolean().default(false),
  isOtc: z.boolean().default(false),
  isPrescriptionRequired: z.boolean().default(false),
  isReturnable: z.boolean().default(true),
  productType: z
    .enum(["MEDICINE", "SURGICAL", "FMCG", "AYURVEDIC", "OTHER"])
    .default("MEDICINE"),
  shelfLifeMonths: positiveInt.optional(),
  storageType: z
    .enum(["ROOM_TEMP", "COOL_DRY", "REFRIGERATED", "FROZEN"])
    .default("ROOM_TEMP"),
  drugGenericId: z.string().uuid().optional(),
  formId: z.string().uuid().optional(),
  hsnCodeId: z.string().uuid().optional(),
  manufacturerId: z.string().uuid().optional(),
});
export type ProductInput = z.infer<typeof productSchema>;

// ── Customer ─────────────────────────────────────────────────────────────

export const customerSchema = z.object({
  name: z.string().min(2, "Customer name is required"),
  phone: indianPhone.optional().or(z.literal("")),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  age: positiveInt.optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  pincode: indianPincode.optional().or(z.literal("")),
  gstNumber,
  creditLimit: positiveDecimal.default(0),
  creditDays: positiveInt.default(0),
  notes: z.string().optional(),
  allergies: z.string().optional(),
  bloodGroup: z.string().optional(),
  dateOfBirth: z.string().optional(), // ISO date string
  gender: z.enum(["MALE", "FEMALE", "OTHER"]).optional(),
  customerGroupId: z.string().uuid().optional(),
  doctorId: z.string().uuid().optional(),
  priceListId: z.string().uuid().optional(),
});
export type CustomerInput = z.infer<typeof customerSchema>;

// ── Supplier ─────────────────────────────────────────────────────────────

export const supplierSchema = z.object({
  name: z.string().min(2, "Supplier name is required"),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  phone: indianPhone,
  contactPerson: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  pincode: indianPincode.optional().or(z.literal("")),
  gstNumber,
  panNumber,
  creditDays: positiveInt.default(30),
  creditLimit: positiveDecimal.default(0),
  isActive: z.boolean().default(true),
  code: z.string().optional(),
  drugLicenseNo: z.string().optional(),
  accountNumber: z.string().optional(),
  bankName: z.string().optional(),
  ifscCode: z
    .string()
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC code")
    .optional()
    .or(z.literal("")),
  deliveryLeadDays: positiveInt.optional(),
  minimumOrderValue: positiveDecimal.optional(),
  returnPolicy: z.string().optional(),
  supplierType: z
    .enum(["DISTRIBUTOR", "MANUFACTURER", "WHOLESALER", "IMPORTER"])
    .default("DISTRIBUTOR"),
});
export type SupplierInput = z.infer<typeof supplierSchema>;

// ── Purchase Order ───────────────────────────────────────────────────────

export const purchaseOrderItemSchema = z.object({
  productId: z.string().uuid("Product is required"),
  productName: z.string().optional(),
  quantity: z.coerce.number().int().min(1, "Quantity must be at least 1"),
  freeQuantity: positiveInt.default(0),
  unitPrice: positiveDecimal,
  discountPercent: z.coerce.number().min(0).max(100).default(0),
  gstPercent: z.coerce.number().min(0).max(100).default(5),
  batchNumber: z.string().optional(),
  expiryDate: z.string().optional(),
  mrp: positiveDecimal.optional(),
});
export type PurchaseOrderItemInput = z.infer<typeof purchaseOrderItemSchema>;

export const purchaseOrderSchema = z.object({
  supplierId: z.string().uuid("Select a supplier"),
  storeId: z.string().uuid("Select a store"),
  expectedDelivery: z.string().optional(),
  notes: z.string().optional(),
  termsConditions: z.string().optional(),
  items: z
    .array(purchaseOrderItemSchema)
    .min(1, "At least one item is required"),
});
export type PurchaseOrderInput = z.infer<typeof purchaseOrderSchema>;

// ── Invoice / Billing ────────────────────────────────────────────────────

export const invoiceItemSchema = z.object({
  productId: z.string().uuid("Product is required"),
  batchId: z.string().uuid().optional(),
  quantity: z.coerce.number().min(1, "Quantity must be at least 1"),
  unitPrice: positiveDecimal,
  discountPercent: z.coerce.number().min(0).max(100).default(0),
  gstPercent: z.coerce.number().min(0).max(100).default(5),
});
export type InvoiceItemInput = z.infer<typeof invoiceItemSchema>;

export const invoiceSchema = z.object({
  customerId: z.string().uuid().optional(),
  invoiceType: z.enum(["SALE", "RETURN"]).default("SALE"),
  paymentMethod: z.enum(["CASH", "CARD", "UPI", "CREDIT", "MIXED"]).optional(),
  notes: z.string().optional(),
  discountSchemeId: z.string().uuid().optional(),
  doctorId: z.string().uuid().optional(),
  hospitalId: z.string().uuid().optional(),
  prescriptionId: z.string().uuid().optional(),
  items: z.array(invoiceItemSchema).min(1, "At least one item is required"),
});
export type InvoiceInput = z.infer<typeof invoiceSchema>;

// ── Store ────────────────────────────────────────────────────────────────

export const storeSchema = z.object({
  name: z.string().min(2, "Store name is required"),
  address: z.string().min(2, "Address is required"),
  city: z.string().min(2, "City is required"),
  state: z.string().min(2, "State is required"),
  pincode: indianPincode,
  phone: indianPhone.optional().or(z.literal("")),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  drugLicenseNumber: z.string().optional(),
  gstNumber,
  isActive: z.boolean().default(true),
});
export type StoreInput = z.infer<typeof storeSchema>;

// ── Doctor ───────────────────────────────────────────────────────────────

export const doctorSchema = z.object({
  name: z.string().min(2, "Doctor name is required"),
  phone: indianPhone.optional().or(z.literal("")),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  specialization: z.string().optional(),
  registrationNumber: z.string().optional(),
  hospitalId: z.string().uuid().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  isActive: z.boolean().default(true),
});
export type DoctorInput = z.infer<typeof doctorSchema>;

// ── Manufacturer ─────────────────────────────────────────────────────────

export const manufacturerSchema = z.object({
  name: z.string().min(2, "Manufacturer name is required"),
  code: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  phone: indianPhone.optional().or(z.literal("")),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  isActive: z.boolean().default(true),
});
export type ManufacturerInput = z.infer<typeof manufacturerSchema>;

// ── Drug Generic ─────────────────────────────────────────────────────────

export const drugGenericSchema = z.object({
  name: z.string().min(2, "Generic name is required"),
  description: z.string().optional(),
  isActive: z.boolean().default(true),
});
export type DrugGenericInput = z.infer<typeof drugGenericSchema>;

// ── Drug License ─────────────────────────────────────────────────────────

export const drugLicenseSchema = z.object({
  licenseNumber: z.string().min(1, "License number is required"),
  licenseType: z.string().min(1, "License type is required"),
  issuingAuthority: z.string().optional(),
  issueDate: z.string().optional(),
  expiryDate: z.string().optional(),
  isActive: z.boolean().default(true),
});
export type DrugLicenseInput = z.infer<typeof drugLicenseSchema>;

// ── HSN Code ─────────────────────────────────────────────────────────────

export const hsnCodeSchema = z.object({
  code: z.string().min(2, "HSN code is required"),
  description: z.string().optional(),
  gstRate: z.coerce.number().min(0).max(100),
});
export type HSNCodeInput = z.infer<typeof hsnCodeSchema>;

// ── Hospital ─────────────────────────────────────────────────────────────

export const hospitalSchema = z.object({
  name: z.string().min(2, "Hospital name is required"),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  phone: indianPhone.optional().or(z.literal("")),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  contactPerson: z.string().optional(),
  isActive: z.boolean().default(true),
});
export type HospitalInput = z.infer<typeof hospitalSchema>;

// ── Rack Location ────────────────────────────────────────────────────────

export const rackLocationSchema = z.object({
  name: z.string().min(1, "Rack name is required"),
  code: z.string().optional(),
  description: z.string().optional(),
  storeId: z.string().uuid("Select a store"),
});
export type RackLocationInput = z.infer<typeof rackLocationSchema>;

// ── Counter ──────────────────────────────────────────────────────────────

export const counterSchema = z.object({
  name: z.string().min(1, "Counter name is required"),
  code: z.string().optional(),
  storeId: z.string().uuid("Select a store"),
  isActive: z.boolean().default(true),
});
export type CounterInput = z.infer<typeof counterSchema>;

// ── User ─────────────────────────────────────────────────────────────────

export const createUserSchema = z.object({
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email required"),
  phone: indianPhone,
  password: z.string().min(8, "Password must be at least 8 characters"),
  role: z.string().min(1, "Role is required"),
  storeIds: z.array(z.string().uuid()).optional(),
  isActive: z.boolean().default(true),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

// ── Discount Scheme ──────────────────────────────────────────────────────

export const discountSchemeSchema = z.object({
  name: z.string().min(2, "Scheme name is required"),
  discountType: z.enum(["PERCENTAGE", "FLAT"]),
  discountValue: positiveDecimal,
  minPurchaseAmount: positiveDecimal.optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  isActive: z.boolean().default(true),
});
export type DiscountSchemeInput = z.infer<typeof discountSchemeSchema>;

// ── Loyalty Program ──────────────────────────────────────────────────────

export const loyaltyProgramSchema = z.object({
  name: z.string().min(2, "Program name is required"),
  pointsPerRupee: positiveDecimal,
  redemptionRate: positiveDecimal,
  minRedemptionPoints: positiveInt.default(100),
  isActive: z.boolean().default(true),
});
export type LoyaltyProgramInput = z.infer<typeof loyaltyProgramSchema>;

// ── Batch ────────────────────────────────────────────────────────────────

export const batchSchema = z.object({
  productId: z.string().uuid("Product is required"),
  batchNumber: z.string().min(1, "Batch number is required"),
  mfgDate: z.string().optional(),
  expiryDate: z.string().min(1, "Expiry date is required"),
  purchasePrice: positiveDecimal,
  sellingPrice: positiveDecimal,
  mrp: positiveDecimal,
  quantity: z.coerce.number().int().min(0, "Quantity must be >= 0"),
});
export type BatchInput = z.infer<typeof batchSchema>;

// ── Tenant Onboarding (Admin) ────────────────────────────────────────────

export const tenantOnboardingSchema = z.object({
  businessName: z.string().min(2, "Business name is required"),
  businessType: z.string().optional(),
  gstNumber,
  drugLicenseNumber: z.string().optional(),
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email required"),
  phoneNumber: indianPhone,
  storeName: z.string().min(2, "Store name is required"),
  storeAddress: z.string().min(2, "Store address is required"),
  pincode: indianPincode,
  city: z.string().min(2, "City is required"),
  state: z.string().min(2, "State is required"),
  planId: z.string().min(1, "Select a plan"),
});
export type TenantOnboardingInput = z.infer<typeof tenantOnboardingSchema>;

// ── Contact Form ─────────────────────────────────────────────────────────

export const contactSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email required"),
  phone: indianPhone.optional().or(z.literal("")),
  subject: z.string().min(2, "Subject is required"),
  message: z.string().min(10, "Message must be at least 10 characters"),
});
export type ContactInput = z.infer<typeof contactSchema>;
